import React, { useEffect, useMemo, useState } from "react";
import {
  useTable,
  usePagination,
  useSortBy,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import moment from "moment";
import Select from "react-select";

const ItemApprovalReport = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedDept, setSelectedDept] = useState([]);
  const [sortType, setSortType] = useState("created_latest");


  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiClient.get(SERVER_BASE + "/get-items-approved", {
          params: { centerCode: code },
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data || []);
      } catch (err) {
        console.error("Error fetching items:", err);
      }
    };
    fetchData();
  }, []);

  // Get unique department list for React Select
  const departmentOptions = useMemo(() => {
    const setDept = new Set();

    data.forEach((item) => {
      if (item.concernedDepartments) {
        item.concernedDepartments
          .split(",")
          .map((d) => d.trim())
          .filter((d) => d.length > 0)
          .forEach((d) => setDept.add(d));
      }
    });

    return Array.from(setDept).map((dep) => ({
      label: dep,
      value: dep,
    }));
  }, [data]);

  // Filter Data
  const filteredData = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
  
    let temp = data.filter((item) => {
      // -----------------------------------------
      // 1️⃣  DEPARTMENT FILTER
      // -----------------------------------------
      if (selectedDept.length > 0) {
        const deptList = (item.concernedDepartments || "")
          .split(",")
          .map((d) => d.trim());
  
        const selectedValues = selectedDept.map((x) => x.value);
  
        const hasMatch = deptList.some((d) => selectedValues.includes(d));
        if (!hasMatch) return false;
      }
  
      // -----------------------------------------
      // 2️⃣  GLOBAL SEARCH
      // -----------------------------------------
      if (!trimmed) return true;
  
      const deptListLower = (item.concernedDepartments || "")
        .split(",")
        .map((d) => d.trim().toLowerCase());
  
      const createdString = `${item.createdByFirstName || ""} ${
        item.createdAt
          ? moment(item.createdAt).format("DD-MMM-YY hh:mm A")
          : ""
      }`.toLowerCase();
  
      const updatedString = `${item.updatedByName || ""} ${
        item.updatedAt
          ? moment(item.updatedAt).format("DD-MMM-YY hh:mm A")
          : ""
      }`.toLowerCase();
  
      const approvedString = `${item.approvedByName || ""} ${
        item.approvedAt
          ? moment(item.approvedAt).format("DD-MMM-YY hh:mm A")
          : ""
      }`.toLowerCase();
  
      return (
        (item.printName || "").toLowerCase().includes(trimmed) ||
        (item.uom || "").toLowerCase().includes(trimmed) ||
        deptListLower.some((d) => d.includes(trimmed)) ||
        (item.itemGroup || "").toLowerCase().includes(trimmed) ||
        (item.type || "").toLowerCase().includes(trimmed) ||
        createdString.includes(trimmed) ||
        updatedString.includes(trimmed) ||
        approvedString.includes(trimmed)
      );
    });
  
    // -----------------------------------------
    // 3️⃣  ADVANCED SORTING (CREATED / UPDATED / APPROVED)
    // -----------------------------------------
  
    temp = temp.sort((a, b) => {
      const cA = new Date(a.createdAt || 0);
      const cB = new Date(b.createdAt || 0);
  
      const uA = new Date(a.updatedAt || 0);
      const uB = new Date(b.updatedAt || 0);
  
      const aA = new Date(a.approvedAt || 0);
      const aB = new Date(b.approvedAt || 0);
  
      switch (sortType) {
        // Created
        case "created_latest":
          return cB - cA;
        case "created_oldest":
          return cA - cB;
  
        // Updated
        case "updated_latest":
          return uB - uA;
        case "updated_oldest":
          return uA - uB;
  
        // Approved
        case "approved_latest":
          return aB - aA;
        case "approved_oldest":
          return aA - aB;
  
        default:
          return 0;
      }
    });
  
    return temp;
  }, [search, data, selectedDept, sortType]);
  
  // Table Columns
  const columns = useMemo(
    () => [
      {
        Header: "S.No",
        accessor: "serial",
        Cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
        disableSortBy: true,
      },
      { Header: "Item Name", accessor: "printName" },
      { Header: "UOM", accessor: "uom" },
      { Header: "Deptt.", accessor: "concernedDepartments" },
      { Header: "Group", accessor: "itemGroup" },
      { Header: "Type", accessor: "type" },

      {
        Header: "Created",
        accessor: (row) => row,
        Cell: ({ value }) => (
          <div>
            {value.createdByFirstName}
            <br />
            <span style={{ color: "#777", fontSize: "13px" }}>
              {value.createdAt
                ? moment(value.createdAt).format("DD-MMM-YY hh:mm A")
                : ""}
            </span>
          </div>
        ),
      },

      {
        Header: "Updated",
        accessor: (row) => row,
        Cell: ({ value }) => (
          <div>
            {value.updatedByName}
            <br />
            <span style={{ color: "#777", fontSize: "13px" }}>
              {value.updatedAt
                ? moment(value.updatedAt).format("DD-MMM-YY hh:mm A")
                : ""}
            </span>
          </div>
        ),
      },

      {
        Header: "Approved",
        accessor: (row) => row,
        Cell: ({ value }) => (
          <div>
            {value.approvedByName}
            <br />
            <span style={{ color: "#777", fontSize: "13px" }}>
              {value.approvedAt
                ? moment(value.approvedAt).format("DD-MMM-YY hh:mm A")
                : ""}
            </span>
          </div>
        ),
      },
    ],
    []
  );

  // Table hook
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    pageOptions,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 50 },
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="formcontainer mt-5">
      <div className="d-flex justify-content-between align-items-center">
        <h3>Item Approval Report</h3>
        <h6>Count: {filteredData.length}</h6>
      </div>

      <div className="d-flex gap-2 mb-3">
        {/* Search */}
        <input
          className="form-control"
          placeholder="Search anything..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* React Select Multi Select */}
        <div style={{ minWidth: "450px" }}>
          <Select
            isMulti
            options={departmentOptions}
            value={selectedDept}
            onChange={(selected) => setSelectedDept(selected || [])}
            placeholder="Filter Departments"
          />
        </div>
        {/* Sort By */}
{/* Sort By */}
<div style={{ minWidth: "180px" }}>
  <select
    className="form-select"
    value={sortType}
    onChange={(e) => setSortType(e.target.value)}
  >
    <option value="created_latest">Created → Latest</option>
    <option value="created_oldest">Created → Oldest</option>
    <option value="approved_latest">Approved → Latest</option>
    <option value="approved_oldest">Approved → Oldest</option>
  </select>
</div>


        {/* Page size */}
        <select
          className="form-select w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          <option value={50}>Show 50</option>
          <option value={75}>Show 75</option>
          <option value={100}>Show 100</option>
          <option value={200}>Show 200</option>
        </select>
      </div>

      {/* Table */}
      <table
        {...getTableProps()}
        className="table table-bordered table-striped"
        style={{ fontSize: "13px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  style={{ cursor: "pointer" }}
                >
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between mt-3">
        <button
          className="btn btn-secondary"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button
          className="btn btn-secondary"
          onClick={nextPage}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ItemApprovalReport;
